import * as Schema from "effect/Schema";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { APP_SIDEBAR_WIDTHS, type AppSidebarSection } from "../components/appSidebarWidth";
import { resolveInboxSidebarWidth } from "../components/inbox/inboxSidebarWidth";
import { resolveInitialThreadSidebarWidth } from "../components/threadSidebarWidth";
import { getLocalStorageItem, setLocalStorageItem } from "./useLocalStorage";

function readPreferredWidth(section: AppSidebarSection): number {
  const { storageKey, defaultWidth } = APP_SIDEBAR_WIDTHS[section];
  try {
    return getLocalStorageItem(storageKey, Schema.Finite) ?? defaultWidth;
  } catch (error) {
    console.error("Could not read persisted sidebar width.", error);
    return defaultWidth;
  }
}

/**
 * Owns committed preferences; the rail owns the temporary drag position.
 * Storage is read once at mount and written once per changed, completed drag.
 * Pointer movement never enters React state or writes to localStorage.
 */
export function useAppSidebarWidth(section: AppSidebarSection, availableWidth: number) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Every section in APP_SIDEBAR_WIDTHS, so a new section can never read an
  // undefined preference (a NaN width hides the sidebar).
  const [preferences, setPreferences] = useState(
    () =>
      Object.fromEntries(
        (Object.keys(APP_SIDEBAR_WIDTHS) as AppSidebarSection[]).map((key) => [
          key,
          readPreferredWidth(key),
        ]),
      ) as Record<AppSidebarSection, number>,
  );
  // Window resizing constrains the display without erasing the preferred width.
  const width =
    section === "inbox"
      ? resolveInboxSidebarWidth(preferences.inbox, availableWidth)
      : resolveInitialThreadSidebarWidth(preferences[section], availableWidth);
  const onResize = useCallback(
    (nextWidth: number) => {
      // A rail click also ends a pointer interaction. It must not replace a
      // wider preference with the width temporarily constrained by the window.
      if (nextWidth === width) return;
      setPreferences((previous) => ({ ...previous, [section]: nextWidth }));
      try {
        setLocalStorageItem(APP_SIDEBAR_WIDTHS[section].storageKey, nextWidth, Schema.Finite);
      } catch (error) {
        console.error("Could not persist sidebar width.", error);
      }
    },
    [section, width],
  );

  useLayoutEffect(() => {
    // The rail writes this variable directly while dragging. Restore it on a
    // section switch even when both saved widths match and React skips the style diff.
    // Viewport changes also cancel the old drag, even if the preferred width still fits.
    const style = wrapperRef.current?.style;
    const cssWidth = `${width}px`;
    // Reading an inline property does not measure layout. Usually React (or
    // the final drag frame) already applied it, so avoid another DOM write.
    if (style && style.getPropertyValue("--sidebar-width") !== cssWidth) {
      style.setProperty("--sidebar-width", cssWidth);
    }
  }, [section, width, availableWidth]);

  return { wrapperRef, width, onResize };
}
